/**
 * The connector's own per-camera report for a site (ENG-2457 step 1). Loads
 * on mount, and again every `refreshMs` while that is set — the onboarding
 * card uses it in the minutes after a first sync, while the connector starts
 * and reports in.
 */
import React from "react";
import { fetchSiteConnectivity } from "./siteConnectivityApi";
import { ConnectivityResult } from "./siteConnectivityTypes";

export type SiteConnectivityLoad =
  | { status: "loading" }
  | { status: "ok"; data: ConnectivityResult }
  | { status: "unavailable" }
  | { status: "error"; message: string };

export const useSiteConnectivity = (
  siteId: number | null,
  { refreshMs }: { refreshMs?: number | null } = {}
): { load: SiteConnectivityLoad; reload: () => void } => {
  const [load, setLoad] = React.useState<SiteConnectivityLoad>({
    status: "loading",
  });
  const [nonce, setNonce] = React.useState(0);

  React.useEffect(() => {
    if (siteId == null) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const fetchOnce = async () => {
      const outcome = await fetchSiteConnectivity(siteId);
      if (cancelled) return;
      if (outcome.kind === "ok") setLoad({ status: "ok", data: outcome.data });
      else if (outcome.kind === "unavailable" || outcome.kind === "not_found")
        setLoad({ status: "unavailable" });
      else if (outcome.kind === "forbidden" || outcome.kind === "error")
        setLoad({ status: "error", message: outcome.message });
      else setLoad({ status: "error", message: "Something went wrong." });
      if (refreshMs) timer = setTimeout(fetchOnce, refreshMs);
    };
    fetchOnce();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [siteId, refreshMs, nonce]);

  // Keep the last answer on screen while a reload is in flight.
  const reload = React.useCallback(() => setNonce((n) => n + 1), []);
  return { load, reload };
};
